/**
 * Pure partition math for the responsive overflow tab bar.
 *
 * Given the available container width and the intrinsic widths of an ordered
 * list of items, decide how many leading items stay visible before the rest
 * collapse into an overflow trigger (a kebab menu). This module is deliberately
 * free of React and the DOM so the (load-bearing) layout decision can be unit
 * tested without a layout engine — happy-dom/jsdom report zero-size rects.
 */

export interface ComputeVisibleCountArgs {
  /** Available inner width of the visible strip, in px. */
  containerWidth: number;
  /** Intrinsic px widths of every item, in strict left-to-right order. */
  itemWidths: number[];
  /** Flex gap between adjacent items, in px (e.g. `gap-0.5` = 2). */
  gap: number;
  /**
   * Width the overflow trigger occupies INCLUDING the gap before it, in px.
   * Reserved only when at least one item must be hidden.
   */
  triggerWidth: number;
  /** Never collapse below this many visible items. Defaults to 1. */
  minVisible?: number;
  /**
   * Index of an item that must always be visible (e.g. the active view). When
   * it falls beyond the visible prefix, it takes the LAST visible slot in
   * place of the item that would otherwise sit there — so the result `count`
   * means "the first `count - 1` items plus the pinned one". Items are never
   * reordered otherwise. Ignored when out of range.
   */
  pinnedIndex?: number;
  /**
   * Fixed width always subtracted from the budget, in px — room the strip
   * needs for chrome that isn't an item (a divider, the growth of a pill on
   * hover). Defaults to 0.
   */
  reserve?: number;
}

/**
 * Compute how many leading items fit, reserving room for the overflow trigger
 * whenever anything has to be hidden. Strict left-to-right: items are never
 * reordered, so the result is always a prefix length `0..itemWidths.length` —
 * except for a `pinnedIndex` item, which may replace the last visible slot.
 */
export function computeVisibleCount(args: ComputeVisibleCountArgs): number {
  const { itemWidths, gap, triggerWidth, reserve = 0 } = args;
  const n = itemWidths.length;

  if (n === 0) {
    return 0;
  }

  // Unmeasured (first paint, or a hidden/detached container) — show everything.
  // This keeps production from collapsing before the first measurement AND
  // keeps existing tests green in environments without a layout engine.
  if (args.containerWidth <= 0) {
    return n;
  }

  const containerWidth = args.containerWidth - reserve;
  const floor = Math.min(args.minVisible ?? 1, n);
  const pinned =
    args.pinnedIndex !== undefined &&
    args.pinnedIndex >= 0 &&
    args.pinnedIndex < n
      ? args.pinnedIndex
      : null;

  // Fast path: if every item fits with no trigger, show them all.
  const totalAll = sumWithGaps(itemWidths, gap, n);
  if (totalAll <= containerWidth) {
    return n;
  }

  // Otherwise the trigger WILL render, so subtract its footprint from the
  // budget and greedily admit items until the next one would overflow. With a
  // pinned item beyond the prefix, each candidate set is the prefix minus its
  // last slot plus the pinned item.
  const budget = containerWidth - triggerWidth;
  let count = 0;
  for (let k = 1; k <= n; k++) {
    const width =
      pinned === null || pinned < k
        ? sumWithGaps(itemWidths, gap, k)
        : sumWithGaps(itemWidths, gap, k - 1) +
          (k > 1 ? gap : 0) +
          itemWidths[pinned];
    if (width > budget) {
      break;
    }
    count = k;
  }

  return Math.max(count, floor);
}

/** Sum the first `count` item widths plus the gaps between them. */
function sumWithGaps(itemWidths: number[], gap: number, count: number): number {
  let total = 0;
  for (let i = 0; i < count; i++) {
    total += itemWidths[i] + (i > 0 ? gap : 0);
  }
  return total;
}
